import React, { useEffect, useState } from 'react';
import { Check, X, Loader2, AlertCircle, UserCheck, UserX, Clock, Calendar, Users } from 'lucide-react';
import api from '../../api/auth.api';
import { API_CONFIG } from '../../config/api.config';
import { EmployeeCorrection, EmployeeReviewStatus, TimeEntryRow, formatHM } from './timecard.types';

interface Props {
  status: EmployeeReviewStatus | null;   // null = employee never opened their review
  loading: boolean;
  onResolved: () => void;                 // refetch status + week after a resolve
  // Everyone's entries for a job/day ("crew times") — supplied by TimeCardEditor
  // from the week entries it already holds; when absent the button is hidden.
  crewEntries?: (jobId: number, ymd: string) => TimeEntryRow[];
  // Whose corrections these are — enables the truck geofence-departure line
  // (employee → assigned truck → GPS stop at the job). Omit to disable.
  employeeId?: number | null;
}

// GET /timecard-review/truck-departure response shape
interface TruckDeparture {
  truck: {
    equipment_id: number;
    equipment_name: string;
    equipment_number?: string | null;
    driver_name?: string | null;   // whose truck (a crew-mate's when not the card's employee)
    is_own_truck?: boolean;
  } | null;
  first_arrival?: string | null;
  last_departure?: string | null;
  visits?: Array<{ arrival?: string | null; departure?: string | null }>;
}

const fmtTime = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' }) : null;
const fmtDate = (d?: string | null) => {
  if (!d) return '—';
  const dt = new Date(`${String(d).slice(0, 10)}T12:00:00`);
  return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};
const fmtDateTime = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

// Long day label ("Friday, Jun 6") for a date-only string ('YYYY-MM-DD').
const fmtDayLong = (d?: string | null) => {
  if (!d) return null;
  const dt = new Date(`${String(d).slice(0, 10)}T12:00:00`);
  return dt.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
};
// Which day a correction applies to: missed_day uses its proposed date, the rest
// use the linked entry's date.
const correctionDay = (c: EmployeeCorrection): string | null => {
  if (c.correction_type === 'missed_day') return fmtDayLong(c.proposed_date);
  return (
    fmtDayLong(c.entry_date) ||
    (c.full_clock_in ? new Date(c.full_clock_in).toLocaleDateString('en-US', { timeZone: 'America/New_York', weekday: 'long', month: 'short', day: 'numeric' }) : null)
  );
};
// entry_date / proposed_date are SQL DATE columns serialized as midnight-UTC ISO
// ("2026-07-07T00:00:00.000Z"). Parsing that as a timestamp renders the PREVIOUS
// day for US viewers — always take just the date part. Only full_clock_in is a
// real timestamp; format it in Eastern like every other time on this screen.
const fmtDayShort = (c: EmployeeCorrection): string => {
  const d = c.correction_type === 'missed_day' ? c.proposed_date : c.entry_date;
  if (d) {
    return new Date(`${String(d).slice(0, 10)}T12:00:00`)
      .toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }
  if (c.full_clock_in) {
    return new Date(c.full_clock_in)
      .toLocaleDateString('en-US', { timeZone: 'America/New_York', weekday: 'short', month: 'short', day: 'numeric' });
  }
  return '';
};

const TYPE_LABEL: Record<EmployeeCorrection['correction_type'], string> = {
  wrong_clock_in: '⏰ Wrong Clock-In Time',
  wrong_clock_out: '⏰ Wrong Clock-Out Time',
  wrong_job: '📍 Wrong Job',
  missed_day: '➕ Add Missing Entry',
  did_not_take_lunch: '🍽️ Did Not Take Lunch',
};

// employee-submitted auto-fix rows are applied server-side; admins shouldn't see them
const isAutoAdjusted = (c: EmployeeCorrection) => /auto-?adjust|auto-?ajustad/i.test(c.employee_notes || '');

function describe(c: EmployeeCorrection): { from: React.ReactNode; to: React.ReactNode } {
  const job = (name?: string | null, num?: string | null, fallbackId?: number | null) => (
    <span>
      {name || (fallbackId ? `Job #${fallbackId}` : '—')}
      {num ? <span style={{ color: '#94a3b8' }}> · {num}</span> : null}
    </span>
  );
  switch (c.correction_type) {
    case 'wrong_clock_in':
      return { from: fmtTime(c.original_clock_in) || '—', to: fmtTime(c.proposed_clock_in) || '—' };
    case 'wrong_clock_out':
      return { from: fmtTime(c.original_clock_out) || '—', to: fmtTime(c.proposed_clock_out) || '—' };
    case 'wrong_job':
      return {
        from: job(c.original_job_name, c.original_job_number, c.original_job_id),
        to: job(c.proposed_job_name, c.proposed_job_number, c.proposed_job_id),
      };
    case 'missed_day':
      return {
        from: <span style={{ fontStyle: 'italic', color: '#9ca3af' }}>No entry</span>,
        to: (
          <span>
            {fmtDate(c.proposed_date)} · {fmtTime(c.proposed_clock_in) || '7:00 AM'} → {fmtTime(c.proposed_clock_out) || '4:00 PM'}
            {c.proposed_job_name ? <span style={{ color: '#94a3b8' }}> · {c.proposed_job_name}</span> : null}
          </span>
        ),
      };
    case 'did_not_take_lunch':
      return { from: '🍽️ 30 min lunch deducted', to: <span style={{ color: '#16a34a' }}>✓ Remove lunch (+30 min)</span> };
    default:
      return { from: '—', to: '—' };
  }
}

// Flag an approval that would produce an impossible/implausible entry — e.g. an
// AM/PM typo (7 PM clock-in vs 5 PM clock-out → negative; or a ~22h overnight roll).
function timeWarning(c: EmployeeCorrection): string | null {
  let ci = c.full_clock_in, co = c.full_clock_out;
  if (c.correction_type === 'wrong_clock_in') ci = c.proposed_clock_in;
  else if (c.correction_type === 'wrong_clock_out') co = c.proposed_clock_out;
  else return null;
  if (!ci || !co) return null;
  const mins = (new Date(co).getTime() - new Date(ci).getTime()) / 60000;
  if (mins <= 0) return 'This would put clock-in after clock-out — check AM/PM.';
  if (mins > 16 * 60) return `This makes a ${(mins / 60).toFixed(1)}h shift — check AM/PM.`;
  return null;
}

const StatusBanner: React.FC<{ status: EmployeeReviewStatus | null; loading: boolean; pendingCount: number }> = ({ status, loading, pendingCount }) => {
  let bg = '#f1f5f9', fg = '#475569', icon = <Clock size={15} />, text = 'Awaiting employee review';
  if (loading) {
    bg = '#f1f5f9'; fg = '#64748b'; icon = <Loader2 size={15} className="spin" />; text = 'Checking employee review…';
  } else if (!status || status.review_status === 'pending') {
    bg = '#f1f5f9'; fg = '#64748b'; icon = <Clock size={15} />; text = 'Awaiting employee review';
  } else if (status.review_status === 'approved') {
    bg = '#dcfce7'; fg = '#166534'; icon = <UserCheck size={15} />;
    text = `Employee approved their time${status.reviewed_at ? ` · ${fmtDateTime(status.reviewed_at)}` : ''}`;
  } else if (status.review_status === 'skipped') {
    bg = '#f1f5f9'; fg = '#64748b'; icon = <UserX size={15} />; text = 'Employee skipped review';
  } else if (status.review_status === 'issues_flagged' || status.review_status === 'resolved') {
    // 'resolved' = backend flipped the review once the last pending correction was
    // handled (add-resolved-review-status.sql). Older rows may still say
    // 'issues_flagged' with zero pending — render those the same way. Either way
    // it counts as the employee having reviewed their time (submitting the
    // corrections WAS the review) — no re-approval is asked of them.
    if (pendingCount > 0) {
      bg = '#ffedd5'; fg = '#9a3412'; icon = <AlertCircle size={15} />;
      text = `${pendingCount} correction${pendingCount === 1 ? '' : 's'} to review`;
    } else {
      bg = '#dcfce7'; fg = '#166534'; icon = <UserCheck size={15} />; text = 'Employee reviewed — corrections resolved';
    }
  }
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: bg, color: fg, borderRadius: 20, padding: '5px 12px', fontSize: 13, fontWeight: 700 }}>
      {icon}{text}
    </div>
  );
};

// Which job/day a correction is about, for the job label + crew-times lookup.
// entry_job_* is the entry's CURRENT job; original/proposed are submit-time snapshots.
const correctionJob = (c: EmployeeCorrection) => ({
  id: c.entry_job_id ?? c.original_job_id ?? c.proposed_job_id ?? null,
  name: c.entry_job_name || c.original_job_name || c.proposed_job_name || null,
  number: c.entry_job_number || c.original_job_number || c.proposed_job_number || null,
});
const correctionYmd = (c: EmployeeCorrection): string | null => {
  const d = c.correction_type === 'missed_day' ? c.proposed_date : c.entry_date;
  if (d) return String(d).slice(0, 10);
  if (c.full_clock_in) {
    // en-CA gives YYYY-MM-DD; pin to Eastern like every date on this screen
    return new Date(c.full_clock_in).toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  }
  return null;
};

// Average of Eastern wall-clock times, e.g. "7:04 AM" (null when list is empty)
const etMinutes = (iso: string): number => {
  const [h, m] = new Date(iso)
    .toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour12: false, hour: '2-digit', minute: '2-digit' })
    .split(':').map(Number);
  return ((h % 24) * 60) + m;
};
const avgTimeLabel = (isos: string[]): string | null => {
  if (isos.length === 0) return null;
  const avg = Math.round(isos.reduce((s, iso) => s + etMinutes(iso), 0) / isos.length);
  return new Date(2000, 0, 1, Math.floor(avg / 60), avg % 60)
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const DENY_REASONS = [
  'Time is correct as recorded',
  'Verified with GPS / geofence',
  'Confirmed with foreman',
  'Already corrected separately',
  'Duplicate or accidental request',
];
const OTHER = '__other__';

export const CorrectionsReview: React.FC<Props> = ({ status, loading, onResolved, crewEntries, employeeId }) => {
  const [busyId, setBusyId] = useState<number | null>(null);
  const [crewOpenId, setCrewOpenId] = useState<number | null>(null);
  // Truck geofence departure per (employee, job, day) — 'loading' while in flight,
  // null on error. Samsara-backed lookups can take a few seconds; keyed cache so
  // revisiting a card never refetches.
  const [truckByKey, setTruckByKey] = useState<Record<string, TruckDeparture | null | 'loading'>>({});
  const [denyId, setDenyId] = useState<number | null>(null);
  const [denyPreset, setDenyPreset] = useState<string>(DENY_REASONS[0]);
  const [denyNotes, setDenyNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const openDeny = (correctionId: number) => {
    setDenyId(correctionId);
    setDenyPreset(DENY_REASONS[0]);
    setDenyNotes('');
    setError(null);
  };
  const denyReason = denyPreset === OTHER ? denyNotes.trim() : denyPreset;

  const all = (status?.corrections || []).filter((c) => !isAutoAdjusted(c));
  const pending = all.filter((c) => c.admin_status === 'pending');
  const resolved = all.filter((c) => c.admin_status !== 'pending');

  // Look up the truck's GPS stop at each pending correction's job/day.
  useEffect(() => {
    if (!employeeId) return;
    for (const c of pending) {
      const job = correctionJob(c);
      const ymd = correctionYmd(c);
      if (job.id == null || !ymd) continue;
      const key = `${employeeId}|${job.id}|${ymd}`;
      if (key in truckByKey) continue;
      setTruckByKey((prev) => ({ ...prev, [key]: 'loading' }));
      api.get(`${API_CONFIG.ENDPOINTS.TIMECARD_TRUCK_DEPARTURE}?employee_id=${employeeId}&job_id=${job.id}&date=${ymd}`)
        .then((res) => setTruckByKey((prev) => ({ ...prev, [key]: res.data?.success ? res.data.data : null })))
        .catch(() => setTruckByKey((prev) => ({ ...prev, [key]: null })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, status?.corrections]);

  const resolve = async (c: EmployeeCorrection, action: 'approved' | 'rejected', notes: string) => {
    if (action === 'rejected' && !notes.trim()) {
      setError('A reason is required to deny a correction.');
      return;
    }
    setBusyId(c.correction_id);
    setError(null);
    try {
      const res = await api.post(API_CONFIG.ENDPOINTS.TIMECARD_RESOLVE, {
        correction_id: c.correction_id,
        action,
        admin_notes: action === 'approved' ? (notes || 'Accepted from timecard review') : notes,
      });
      if (!res.data.success) throw new Error(res.data.error || 'Failed to resolve');
      setDenyId(null);
      setDenyNotes('');
      onResolved();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to resolve correction.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={{ marginTop: 14 }}>
      <StatusBanner status={status} loading={loading} pendingCount={pending.length} />

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 8, padding: '8px 10px', fontSize: 13, margin: '10px 0' }}>
          {error}
        </div>
      )}

      {/* Pending corrections — actionable */}
      {pending.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
          {pending.map((c) => {
            const { from, to } = describe(c);
            const busy = busyId === c.correction_id;
            const warn = timeWarning(c);
            const job = correctionJob(c);
            const ymd = correctionYmd(c);
            const canCrew = !!crewEntries && job.id != null && !!ymd;
            const truckKey = employeeId && job.id != null && ymd ? `${employeeId}|${job.id}|${ymd}` : null;
            const truck = truckKey ? truckByKey[truckKey] : undefined;
            return (
              <div key={c.correction_id} style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: 12 }}>
                {correctionDay(c) && (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#fff', border: '1px solid #fde68a', borderRadius: 8, padding: '3px 9px', fontSize: 13, fontWeight: 700, color: '#1f2937', marginBottom: 8 }}>
                    <Calendar size={13} color="#d97706" /> {correctionDay(c)}
                  </div>
                )}
                {job.name && (
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
                    📍 {job.name}{job.number ? <span style={{ color: '#94a3b8' }}> · #{job.number}</span> : null}
                  </div>
                )}
                {/* Assigned truck's GPS stop at this job — silent when the employee
                    has no telematics-equipped truck */}
                {truck === 'loading' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#94a3b8', marginBottom: 6 }}>
                    🚚 <Loader2 size={13} className="spin" /> Checking truck GPS…
                  </div>
                )}
                {truck && truck !== 'loading' && truck.truck && (
                  <div style={{ fontSize: 13, color: '#475569', marginBottom: 6 }}>
                    🚚 <strong>{truck.truck.equipment_name}</strong>
                    {truck.truck.is_own_truck === false && truck.truck.driver_name ? (
                      <span style={{ color: '#94a3b8' }}> · {truck.truck.driver_name}’s truck</span>
                    ) : null}
                    {(truck.visits?.length ?? 0) > 0 ? (
                      <>
                        {' — '}
                        {truck.last_departure
                          ? <>left this job at <strong>{fmtTime(truck.last_departure)}</strong></>
                          : 'still at this job at last GPS point'}
                        {truck.first_arrival ? ` (arrived ${fmtTime(truck.first_arrival)})` : ''}
                      </>
                    ) : (
                      ' — no GPS stop at this job that day'
                    )}
                  </div>
                )}
                <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e', marginBottom: 6 }}>{TYPE_LABEL[c.correction_type]}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#1f2937', flexWrap: 'wrap' }}>
                  <span style={{ textDecoration: 'line-through', color: '#9ca3af' }}>{from}</span>
                  <span style={{ color: '#64748b' }}>→</span>
                  <span style={{ fontWeight: 700 }}>{to}</span>
                </div>
                {c.employee_notes && (
                  <div style={{ fontSize: 13, color: '#475569', marginTop: 6, fontStyle: 'italic' }}>“{c.employee_notes}”</div>
                )}
                {warn && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, padding: '7px 10px', borderRadius: 8, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 13, fontWeight: 600 }}>
                    <AlertCircle size={14} /> {warn}
                  </div>
                )}

                {/* Crew times at this job that day — same context as bfav5's "Time card" view */}
                {canCrew && (
                  <button
                    onClick={() => setCrewOpenId(crewOpenId === c.correction_id ? null : c.correction_id)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8, padding: '6px 10px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#4f46e5', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                  >
                    <Users size={14} /> {crewOpenId === c.correction_id ? 'Hide crew times' : 'Crew times'}
                  </button>
                )}
                {canCrew && crewOpenId === c.correction_id && (() => {
                  const rows = crewEntries!(job.id!, ymd!)
                    .slice()
                    .sort((a, b) => new Date(a.clock_in).getTime() - new Date(b.clock_in).getTime());
                  if (rows.length === 0) {
                    return <div style={{ marginTop: 8, fontSize: 13, color: '#64748b' }}>No entries at this job that day.</div>;
                  }
                  const avgIn = avgTimeLabel(rows.map((r) => r.clock_in));
                  const avgOut = avgTimeLabel(rows.filter((r) => r.clock_out).map((r) => r.clock_out as string));
                  return (
                    <div style={{ marginTop: 8, background: '#fff', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 10px' }}>
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: '#92400e', marginBottom: 6 }}>
                        Crew at this job · avg in {avgIn ?? '—'}{avgOut ? ` · avg out ${avgOut}` : ''}
                      </div>
                      {rows.map((r) => {
                        const mine = c.entry_id != null && r.entry_id === c.entry_id;
                        return (
                          <div key={r.entry_id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 13, padding: '3px 0', color: mine ? '#1f2937' : '#475569', fontWeight: mine ? 700 : 500 }}>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {r.first_name} {r.last_name}{mine ? ' ←' : ''}
                            </span>
                            <span style={{ whiteSpace: 'nowrap' }}>
                              {fmtTime(r.clock_in)} – {r.clock_out ? fmtTime(r.clock_out) : 'open'}
                              {r.clock_out ? ` · ${formatHM(Number(r.hours_worked) || 0)}` : ''}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {denyId === c.correction_id ? (
                  <div style={{ marginTop: 10 }}>
                    <select
                      value={denyPreset}
                      onChange={(e) => setDenyPreset(e.target.value)}
                      style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 14, boxSizing: 'border-box', marginBottom: 8, background: '#fff', fontWeight: 600 }}
                    >
                      {DENY_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                      <option value={OTHER}>Other…</option>
                    </select>
                    {denyPreset === OTHER && (
                      <input
                        type="text" value={denyNotes} placeholder="Reason for denial (required)" autoFocus
                        onChange={(e) => setDenyNotes(e.target.value)}
                        style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 14, boxSizing: 'border-box', marginBottom: 8 }}
                      />
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => resolve(c, 'rejected', denyReason)} disabled={busy}
                        style={{ flex: 1, padding: 10, borderRadius: 8, border: 'none', cursor: 'pointer', background: '#dc2626', color: '#fff', fontWeight: 700 }}>
                        {busy ? <Loader2 size={15} className="spin" /> : 'Confirm deny'}
                      </button>
                      <button onClick={() => { setDenyId(null); setDenyNotes(''); setError(null); }} disabled={busy}
                        style={{ flex: 1, padding: 10, borderRadius: 8, border: '1px solid #e5e7eb', cursor: 'pointer', background: '#fff', color: '#475569', fontWeight: 600 }}>
                        Back
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button onClick={() => resolve(c, 'approved', '')} disabled={busy}
                      style={{ flex: 1, padding: 10, borderRadius: 8, border: 'none', cursor: 'pointer', background: '#16a34a', color: '#fff', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                      {busy ? <Loader2 size={15} className="spin" /> : <Check size={16} />} Accept &amp; apply
                    </button>
                    <button onClick={() => openDeny(c.correction_id)} disabled={busy}
                      style={{ flex: 1, padding: 10, borderRadius: 8, border: '1px solid #e5e7eb', cursor: 'pointer', background: '#fff', color: '#dc2626', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                      <X size={16} /> Deny
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Resolved corrections — read-only history */}
      {resolved.length > 0 && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {resolved.map((c) => {
            const { from, to } = describe(c);
            const ok = c.admin_status === 'approved' || c.admin_status === 'modified';
            return (
              <div key={c.correction_id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#64748b', padding: '6px 10px', background: '#f8fafc', borderRadius: 8 }}>
                <span style={{ fontWeight: 700, color: ok ? '#16a34a' : '#dc2626' }}>
                  {ok ? 'Approved' : 'Denied'}
                </span>
                <span style={{ color: '#94a3b8' }}>·</span>
                {fmtDayShort(c) && <span style={{ fontWeight: 600, color: '#475569' }}>{fmtDayShort(c)}</span>}
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {TYPE_LABEL[c.correction_type].replace(/^[^ ]+ /, '')}: {from} → {to}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
